// src/lib/reportingCore.js
//
// One shared source of truth for “Reports math” so Dashboard can reuse it
// without duplicating logic. This mirrors the behavior in Reports.jsx.
//
// External deps you already have:
// - firebase/firestore (db passed in)
// - ./budgetMath (for budget map math)
// - ../getSubcategoryStatus (for MTD targets / pacing)
//
// Usage (example):
//   import * as rc from "./lib/reportingCore";
//   const { budgetDocs } = await rc.loadBudgetDocs(db, uid);
//   const rows = await rc.loadClassifiedRows(db, uid, { start, end, confirmedOnly: true }, budgetDocs);
//   const totals = rc.sumTotals(rows);
//   const monthly = rc.groupMonthly(rows, { start, end, daily: false });
//
// For Budget vs Actual across months:
//   const months = rc.monthKeysBetween(start, end);
//   const bva = rc.computeBVA({ months, rows, budgetDocs, includeCurrentMonthMTD: !completedOnly });
//
// Notes:
// - Savings are treated as outflow (same as Reports).
// - Non-budget categories are excluded by default (same as Reports).
// - Category/subcategory filters can be applied via rc.filterRowsBySelection and
//   passed to BVA helpers via the same `keepBudgetEntry` predicate as Reports.

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  computeBudgetForMonthList,
  parseKey,
  parseMonthKey,
} from "./budgetMath";
import { getSubcategoryStatus } from "../getSubcategoryStatus";

/* ---------------------- label + misc helpers (mirrors Reports) ---------------------- */

export function normalizeLabel(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const NON_BUDGET_CATEGORY_NAMES = [
  "Transfers & Reimbursements",
  "Transfers & Payments (Not in Budget)",
  "Items That Do Not Impact Budget",
  "Non-Budget",
];
const NON_BUDGET_SET = new Set(NON_BUDGET_CATEGORY_NAMES.map(normalizeLabel));

function ymKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const startOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

export function monthKeysBetween(startDate, endDate) {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const arr = [];
  for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    arr.push(ymKey(d));
  }
  return arr;
}

export function daysBetweenKeys(startDate, endDate) {
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );
  const arr = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    arr.push(dayKey(d));
  }
  return arr;
}

// Prefer category/subcategory from your match fields when available (same as Reports)
function pickCatSub(d) {
  const cm = d?.categoryMatch || {};
  const catFromMatch = (cm.category || "").trim();
  const subFromMatch = (cm.subcategory || "").trim();
  if (catFromMatch) {
    return {
      category: catFromMatch,
      subcategory: subFromMatch || String(d?.subcategory || ""),
    };
  }
  if (typeof d?.category === "string" && d.category.trim()) {
    return { category: d.category, subcategory: String(d?.subcategory || "") };
  }
  if (Array.isArray(d?.category) && d.category.length) {
    const label = String(d.category[d.category.length - 1] ?? d.category[0]);
    return { category: label, subcategory: String(d?.subcategory || "") };
  }
  return {
    category: String(d?.category || ""),
    subcategory: String(d?.subcategory || ""),
  };
}

/* ---------------------- budgets → type index (mirrors Reports) ---------------------- */

export function buildTypeIndexFromBudgets(budgetDocsSnapDocs) {
  const ix = new Map();
  for (const d of budgetDocsSnapDocs) {
    const b = d.data();
    const cat = b?.category;
    const catNorm = normalizeLabel(cat);
    const type = String(b?.type || "").trim().toLowerCase(); // income | expense | savings | non-budget
    if (catNorm && type) ix.set(catNorm, type);
    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
    for (const s of subs) {
      const subNorm = normalizeLabel(s?.name);
      if (catNorm && subNorm && type) ix.set(`${catNorm}::${subNorm}`, type);
    }
  }
  return ix;
}

export async function loadBudgetDocs(db, uid) {
  const budgetsQ = query(collection(db, "budgets"), where("userId", "==", uid));
  const snap = await getDocs(budgetsQ);
  return {
    snap,                            // original QuerySnapshot (for indexes)
    budgetDocs: snap.docs.map((d) => d.data()), // plain docs (for math)
    typeIndex: buildTypeIndexFromBudgets(snap.docs),
  };
}

/* ---------------------- transactions → classified rows (mirrors Reports) ---------------------- */

function classifyType(tx, typeIx) {
  const catNorm = normalizeLabel(tx.category);
  const subNorm = normalizeLabel(tx.subcategory);
  if (catNorm && subNorm) {
    const t = typeIx.get(`${catNorm}::${subNorm}`);
    if (t) return t;
  }
  if (catNorm) {
    const t = typeIx.get(catNorm);
    if (t) return t;
  }
  return null; // unmapped
}

export async function loadClassifiedRows(
  db,
  uid,
  { start, end, confirmedOnly = true },
  budgetDocs // array from loadBudgetDocs().budgetDocs (only needed to build filter UI elsewhere)
) {
  const txSnap = await getDocs(
    query(collection(db, "transactions"), where("userId", "==", uid))
  );

  // If caller didn’t pass a type index, build it quickly here from live budgets
  let typeIx;
  if (!typeIx) {
    const budgetsQ = query(
      collection(db, "budgets"),
      where("userId", "==", uid)
    );
    const budgetsSnap = await getDocs(budgetsQ);
    typeIx = buildTypeIndexFromBudgets(budgetsSnap.docs);
  }

  const startMs = startOfDay(start).getTime();
  const endMs = endOfDay(end).getTime();

  const includedRows = [];
  let excludedNonBudget = 0;
  let excludedNonBudgetByName = 0;

  txSnap.forEach((doc) => {
    const d = doc.data();
    const when = d?.date?.toDate ? d.date.toDate() : null;
    if (!when) return;
    const t = when.getTime();
    if (t < startMs || t > endMs) return;

    // Confirmed-only semantics: same as Reports (“confirmed | rule | wizard | manual” or status: confirmed)
    const txType = String(d?.type || "").toLowerCase();
    const status = String(d?.status || "").toLowerCase();
    const source = String(d?.source || "").toLowerCase();
    const effectiveType = txType || (source === "manual-upload" ? "manual" : "");
    const allowedTypes = new Set(["confirmed", "rule", "wizard", "manual"]);
    if (confirmedOnly && !allowedTypes.has(effectiveType) && status !== "confirmed") return;

    const { category, subcategory } = pickCatSub(d);
    const row = {
      id: doc.id,
      amount: Number(d?.amount ?? 0),
      date: when,
      vendor: d?.vendor ?? "",
      category,
      subcategory,
    };

    // Map to income/expense/savings/non-budget using budgets
    const mappedType = classifyType(row, typeIx);
    if (mappedType === "non-budget") {
      excludedNonBudget++;
      return;
    }
    if (!mappedType) {
      // Unmapped: by-name non-budget?
      const catNorm = normalizeLabel(row.category);
      const subNorm = normalizeLabel(row.subcategory);
      if (NON_BUDGET_SET.has(catNorm) || NON_BUDGET_SET.has(subNorm)) {
        excludedNonBudgetByName++;
        return;
      }
      // Sign fallback (same as Reports)
      const inferred = row.amount > 0 ? "income" : "expense";
      includedRows.push({ ...row, _type: inferred });
      return;
    }
    includedRows.push({ ...row, _type: mappedType });
  });

  return { rows: includedRows, meta: { excludedNonBudget, excludedNonBudgetByName } };
}

/* ---------------------- filters (same semantics as Reports) ---------------------- */

export function filterRowsBySelection(
  rows,
  { selectedCats = new Set(), selectedSubs = new Set(), includeChildren = true }
) {
  const hasSubs = selectedSubs.size > 0;
  const hasCats = selectedCats.size > 0;
  if (!hasSubs && !hasCats) return rows;

  if (hasSubs) {
    return rows.filter((r) => {
      const c = normalizeLabel(r.category);
      const s = normalizeLabel(r.subcategory);
      return selectedSubs.has(`${c}::${s}`);
    });
  }

  // categories
  if (hasCats) {
    if (includeChildren) {
      return rows.filter((r) => selectedCats.has(normalizeLabel(r.category)));
    }
    // category-only (no children): keep rows that have no subcategory label
    return rows.filter((r) => {
      const c = normalizeLabel(r.category);
      const s = normalizeLabel(r.subcategory);
      return selectedCats.has(c) && !s;
    });
  }

  return rows;
}

// Same predicate used for filtering budget entries inside BvA and Beats/Misses
export function makeKeepBudgetEntryPredicate({
  selectedCats = new Set(),
  selectedSubs = new Set(),
  includeChildren = true,
}) {
  const hasSubs = selectedSubs.size > 0;
  const hasCats = selectedCats.size > 0;
  return (type, category, subcategory) => {
    if (String(type).toLowerCase() === "non-budget") return false;
    const cNorm = normalizeLabel(category);
    const sNorm = normalizeLabel(subcategory);

    if (!hasSubs && !hasCats) return true;
    if (hasSubs) return selectedSubs.has(`${cNorm}::${sNorm}`);
    if (hasCats) {
      if (includeChildren) return selectedCats.has(cNorm);
      return selectedCats.has(cNorm) && !sNorm;
    }
    return true;
  };
}

/* ---------------------- totals + groupings ---------------------- */

export function sumTotals(rows) {
  let income = 0;
  let expenses = 0;
  for (const r of rows) {
    if (r._type === "income") income += r.amount;
    else expenses += r.amount; // negative for spend
  }
  return { income, expenses, net: income + expenses };
}

export function groupMonthly(rows, { start, end, daily = false }) {
  const keys = daily ? daysBetweenKeys(start, end) : monthKeysBetween(start, end);
  const agg = new Map(keys.map((k) => [k, { month: k, income: 0, expenses: 0, net: 0 }]));

  for (const r of rows) {
    const k = daily ? dayKey(r.date) : ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
    if (!agg.has(k)) continue;
    const rec = agg.get(k);
    if (r._type === "income") rec.income += r.amount;
    else rec.expenses += r.amount;
    rec.net = rec.income + rec.expenses;
  }
  return keys.map((k) => agg.get(k));
}

/* ---------------------- Daily digest helpers (NEW) ---------------------- */

// Month boundaries and “now” helpers
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfToday(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

// Build an index of actual MTD spend magnitudes by "cat::sub" for the given rows
function buildActualsIndex(rows) {
  const ix = new Map();
  for (const r of rows) {
    // We only consider expense magnitude for budget alerting
    if (r._type !== "income") {
      const key = `${normalizeLabel(r.category)}::${normalizeLabel(r.subcategory)}`;
      const prev = ix.get(key) || 0;
      ix.set(key, prev + Math.abs(Number(r.amount || 0)));
    }
  }
  return ix;
}

// Build per-type MTD actuals by "cat::sub"
function buildActualsByTypeIndex(rows) {
  const expenseIx = new Map();
  const incomeIx = new Map();
  const savingsIx = new Map(); // rows classified as "savings"

  for (const r of rows) {
    const key = `${normalizeLabel(r.category)}::${normalizeLabel(r.subcategory)}`;
    const amt = Number(r.amount || 0);

    if (r._type === "income") {
      const prev = incomeIx.get(key) || 0;
      incomeIx.set(key, prev + Math.max(0, amt)); // income is positive
    } else if (r._type === "savings") {
      const prev = savingsIx.get(key) || 0;
      savingsIx.set(key, prev + Math.abs(amt)); // treat as magnitude saved
    } else {
      const prev = expenseIx.get(key) || 0;
      expenseIx.set(key, prev + Math.abs(amt)); // expense magnitude
    }
  }
  return { expenseIx, incomeIx, savingsIx };
}

// Load raw transactions that are "needs action" (no schema change)
async function loadNeedsActionTx(db, uid, sinceDate) {
  const snap = await getDocs(query(collection(db, "transactions"), where("userId", "==", uid)));
  const sinceMs = sinceDate ? sinceDate.getTime() : 0;
  const out = [];
  snap.forEach((docSnap) => {
    const d = docSnap.data();
    // date
    const when = d?.date?.toDate ? d.date.toDate() : null;
    if (!when) return;
    if (when.getTime() < sinceMs) return;

    const status = String(d?.status || "").toLowerCase();
    if (status !== "needs_action") return;

    // snooze suppression
    const snooze = d?.snoozeUntil?.toDate ? d.snoozeUntil.toDate() : null;
    if (snooze && snooze.getTime() > Date.now()) return;

    out.push({
      id: docSnap.id,
      amount: Number(d?.amount ?? 0),
      date: when,
      vendor: String(d?.vendor || ""),
      // keep what your dashboard expects to render a row
      category: d?.category || "",
      subcategory: d?.subcategory || "",
      suggested: d?.categoryMatch ? {
        category: d.categoryMatch.category || "",
        subcategory: d.categoryMatch.subcategory || "",
      } : null,
      reason: d?.reviewMessage || "",   // optional hint you show on dashboard
    });
  });
  // newest first
  out.sort((a, b) => b.date.getTime() - a.date.getTime());
  return out;
}

/* ---------------------- Budget vs Actual (per month keys) ---------------------- */

// expected-to-date for a single subcategory row using the same pacing math as Reports
function expectedToDateForSub(sub, isIncome) {
  const { meta } = getSubcategoryStatus({
    subcategory: sub,
    actual: 0,
    isIncome,
  });
  return Number(meta?.expectedForRollup || 0);
}

// Prefer snapshot doc if present: users/{uid}/monthlyBudgets/{YYYY-MM} with { totalsByKey }
async function getBudgetMapForMonthKey(db, uid, mKey, budgetDocs) {
  try {
    const ref = doc(db, "users", uid, "monthlyBudgets", mKey);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const obj = snap.data()?.totalsByKey || {};
      return new Map(Object.entries(obj).map(([k, v]) => [k, Number(v) || 0]));
    }
  } catch {
    // ignore; fall through to live compute
  }
  const { y, m } = parseMonthKey(mKey);
  return computeBudgetForMonthList(budgetDocs, y, m);
}

/**
 * Compute Budget vs Actual for a set of month keys.
 *
 * @param {Object} args
 * @param {string[]} args.months - ["YYYY-MM", ...]
 * @param {Array} args.rows - classified tx rows (signed; income +, expenses -)
 * @param {Array} args.budgetDocs - raw budget docs array (from Firestore)
 * @param {Object} args.filter - { selectedCats, selectedSubs, includeChildren }
 * @param {boolean} args.includeCurrentMonthMTD - if true and months contains now, use expected-to-date pacing for budget in the current month
 * @param {Object} args.db - Firestore db (for reading snapshots)
 * @param {string} args.uid - current user id (for reading snapshots)
 */
export async function computeBVA({
  months,
  rows,
  budgetDocs,
  filter = {},
  includeCurrentMonthMTD = false,
  db,
  uid,
}) {
  if (!months?.length) return { months: [], rows: [] };

  const keepBudgetEntry = makeKeepBudgetEntryPredicate(filter);
  const nowKey = ymKey(new Date());

  // 1) Budget per month
  const budgetByMonth = new Map();
  for (const mKey of months) {
    let inc = 0,
      exp = 0;

    const isCurrentMTD = includeCurrentMonthMTD && mKey === nowKey;
    if (isCurrentMTD) {
      // MTD target from raw budget docs (expected-to-date)
      for (const b of budgetDocs) {
        const type = String(b?.type || "").toLowerCase();
        const category = b?.category || "";
        if (!category || type === "non-budget") continue;
        const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
        for (const sub of subs) {
          const name = sub?.name || "";
          if (!keepBudgetEntry(type, category, name)) continue;
          const isInc = type === "income";
          const amt = expectedToDateForSub(sub, isInc);
          if (isInc) inc += Number(amt) || 0;
          else exp += Number(amt) || 0;
        }
      }
    } else {
      // Completed months (or any non-current): use snapshots if present, otherwise compute live
      const mm = await getBudgetMapForMonthKey(db, uid, mKey, budgetDocs);
      if (mm instanceof Map) {
        for (const [k, v] of mm.entries()) {
          const { type, category, subcategory } = parseKey(k);
          if (!keepBudgetEntry(type, category, subcategory)) continue;
          const amt = Number(v) || 0;
          if (String(type).toLowerCase() === "income") inc += amt;
          else exp += amt;
        }
      }
    }
    budgetByMonth.set(mKey, { inc, exp, net: inc - exp });
  }

  // 2) Actual per month (rows already filtered by selection if desired)
  const monthsSet = new Set(months);
  const actualByMonth = new Map();
  for (const r of rows) {
    const mk = ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
    if (!monthsSet.has(mk)) continue;
    const rec = actualByMonth.get(mk) || { inc: 0, exp: 0 };
    if (r._type === "income") rec.inc += r.amount;
    else rec.exp += -r.amount; // magnitude
    actualByMonth.set(mk, rec);
  }
  for (const k of months) {
    const rec = actualByMonth.get(k) || { inc: 0, exp: 0 };
    rec.net = rec.inc - rec.exp;
    actualByMonth.set(k, rec);
  }

  // 3) Compose rows
  const outRows = months.map((k) => {
    const b = budgetByMonth.get(k) || { inc: 0, exp: 0, net: 0 };
    const a = actualByMonth.get(k) || { inc: 0, exp: 0, net: 0 };
    return {
      month: k,
      bIncome: b.inc,
      bExpense: b.exp,
      bNet: b.net,
      aIncome: a.inc,
      aExpense: a.exp,
      aNet: a.net,
      variance: a.net - b.net, // positive favorable in “net” view
    };
  });

  return { months, rows: outRows };
}

/**
 * Build the data for the Daily Alerts email (MVP).
 *
 * - needsAction: up to `maxTx` transactions with status=="needs_action" (not snoozed)
 * - nearBudget: subcategories where actual MTD / monthlyBudget >= nearPct (default 0.90)
 * - overPace:   subcategories where actual MTD > expectedMTD * (1 + overPaceSlack) (default 5%)
 *
 * Reuses:
 * - getSubcategoryStatus() for expected-to-date pacing
 * - computeBudgetForMonthList() / monthlyBudgets snapshot for monthly budgets
 * - loadClassifiedRows() to compute MTD actuals
 */
export async function buildDailyDigestData(
  db,
  uid,
  {
    today = new Date(),
    needsLookbackDays = 90,
    maxTx = 10,
    nearPct = 0.90,
    overPaceSlack = 0.05,
  } = {}
) {
  // 1) Needs Action (limit)
  const since = new Date(today.getTime() - needsLookbackDays * 24 * 60 * 60 * 1000);
  const allNeeds = await loadNeedsActionTx(db, uid, since);
  const needsAction = allNeeds.slice(0, maxTx);

  // 2) Budgets + MTD actuals (current month)
  const { budgetDocs } = await loadBudgetDocs(db, uid);
  const mStart = startOfMonth(today);
  const mEnd = endOfToday(today);

  // Actuals (confirmed-only for MTD budget comparisons)
  const { rows: monthRows } = await loadClassifiedRows(
    db,
    uid,
    { start: mStart, end: mEnd, confirmedOnly: true },
    budgetDocs
  );
  const { expenseIx, incomeIx, savingsIx } = buildActualsByTypeIndex(monthRows);

  // Budget map for this month (prefer snapshot)
  const mKey = `${mStart.getFullYear()}-${String(mStart.getMonth() + 1).padStart(2, "0")}`;
  const budgetMap = await getBudgetMapForMonthKey(db, uid, mKey, budgetDocs); // Map(key => amount)

  // Build a quick lookup from (cat::sub) -> budget sub object (to feed getSubcategoryStatus for expectedMTD)
  const subMeta = new Map(); // "cat::sub" -> the sub object from budgetDocs
  for (const b of budgetDocs) {
    const type = String(b?.type || "").toLowerCase();
    if (type === "non-budget") continue;
    const cat = String(b?.category || "");
    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
    for (const s of subs) {
      const key = `${normalizeLabel(cat)}::${normalizeLabel(s?.name || "")}`;
      if (!subMeta.has(key)) subMeta.set(key, s);
    }
  }

  const nearBudget = [];
  const overPace = [];

  const incomeOffPace = [];
  const goalsOffPace = [];

  if (budgetMap instanceof Map) {
    // Slack knobs for MVP: allow a small buffer before flagging shortfalls
    const incomeSlack = 0.02; // 2% below expected ok
    const goalsSlack  = 0.02; // 2% below expected ok

    for (const [key, monthlyBudgetRaw] of budgetMap.entries()) {
      const { type, category, subcategory } = parseKey(key);
      const t = String(type).toLowerCase();
      const catNorm = normalizeLabel(category);
      const subNorm = normalizeLabel(subcategory);
      const ixKey = `${catNorm}::${subNorm}`;

      // Pull the budget subobject (for expected MTD calc)
      const sub = subMeta.get(ixKey);

      if (t === "income") {
        // Income shortfall: actual income MTD < expected MTD (with tiny slack)
        if (!sub) continue;
        const { meta } = getSubcategoryStatus({ subcategory: sub, actual: 0, isIncome: true });
        const expectedMTD = Number(meta?.expectedForRollup || 0);
        const actualInc = Math.max(0, Number(incomeIx.get(ixKey) || 0));
        const threshold = expectedMTD * (1 - incomeSlack);
        if (expectedMTD > 0 && actualInc < threshold) {
          incomeOffPace.push({
            scopeType: "subcategory",
            category,
            subcategory,
            mtd: actualInc,
            expectedMTD,
            shortBy: expectedMTD - actualInc,
          });
        }
        continue;
      }

      if (t === "savings" || t === "goal") {
        // Goals behind pace: actual saved MTD < expected saved MTD (with tiny slack)
        if (!sub) continue;
        const { meta } = getSubcategoryStatus({ subcategory: sub, actual: 0, isIncome: false });
        const expectedMTD = Number(meta?.expectedForRollup || 0);
        const actualSaved = Math.max(0, Number(savingsIx.get(ixKey) || 0));
        const threshold = expectedMTD * (1 - goalsSlack);
        if (expectedMTD > 0 && actualSaved < threshold) {
          goalsOffPace.push({
            scopeType: "subcategory",
            category,
            subcategory,
            mtd: actualSaved,
            expectedMTD,
            behindBy: expectedMTD - actualSaved,
          });
        }
        continue;
      }

      // ---- Expenses (existing behavior) ----
      const monthlyBudget = Math.max(0, Number(monthlyBudgetRaw) || 0);
      if (monthlyBudget <= 0) continue;

      const actualMTD = Math.max(0, Number(expenseIx.get(ixKey) || 0));

      // A) Near budget (actual vs full-month budget)
      const pctOfBudget = monthlyBudget > 0 ? actualMTD / monthlyBudget : 0;
      if (pctOfBudget >= nearPct) {
        nearBudget.push({
          scopeType: "subcategory",
          category,
          subcategory,
          mtd: actualMTD,
          monthlyBudget,
          pctOfBudget,
        });
      }

      // B) Over expected pacing (actual vs expected-to-date)
      if (sub) {
        const { meta } = getSubcategoryStatus({ subcategory: sub, actual: 0, isIncome: false });
        const expectedMTD = Number(meta?.expectedForRollup || 0);
        const threshold = expectedMTD * (1 + overPaceSlack);
        if (expectedMTD > 0 && actualMTD > threshold) {
          overPace.push({
            scopeType: "subcategory",
            category,
            subcategory,
            mtd: actualMTD,
            expectedMTD,
            overBy: actualMTD - expectedMTD,
            pctOver: (actualMTD - expectedMTD) / expectedMTD,
          });
        }
      }
    }
  }

  // Sort for nice presentation
  nearBudget.sort((a, b) => b.pctOfBudget - a.pctOfBudget);
  overPace.sort((a, b) => b.overBy - a.overBy);

  incomeOffPace.sort((a, b) => b.shortBy - a.shortBy);
  goalsOffPace.sort((a, b) => b.behindBy - a.behindBy); 

  // Compose subject hint
  const hasNeeds = needsAction.length > 0;
  const hasBudget = nearBudget.length > 0 || overPace.length > 0;
  let subjectHint = "none";
  if (hasNeeds && hasBudget) subjectHint = "needs_plus_budget";
  else if (hasNeeds) subjectHint = "needs_only";
  else if (hasBudget) subjectHint = "budget_only";

  return {
    needsAction,
    nearBudget,
    overPace,
    incomeOffPace,
    goalsOffPace,
    subjectHint,
    meta: {
      monthKey: mKey,
      lookedBackDays: needsLookbackDays,
      thresholds: { nearPct, overPaceSlack },
    },
  };
}